import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Users, Trash2 } from "lucide-react";
import { authApi, errMsg } from "../api";

const AccountRow = ({ account, busy, onRemove, onApproval }) => (
  <div
    data-testid={`account-row-${account.email}`}
    className="py-3 flex items-center justify-between gap-3 text-sm"
  >
    <span className="min-w-0">
      <span className="block text-[#F5F5F5] truncate">
        {account.name}
        {account.is_you && <span className="text-[#A3A3A3]"> · you</span>}
      </span>
      <span className="block text-[#A3A3A3] truncate">{account.email}</span>
      <span
        className={`block text-[10px] uppercase tracking-widest mt-1 ${
          account.approved ? "text-[#A3A3A3]" : "text-[#FFD60A]"
        }`}
        data-testid={`account-status-${account.email}`}
      >
        {account.role} · {account.approved ? "approved" : "pending approval"}
      </span>
    </span>
    <span className="flex items-center gap-2 shrink-0">
      {!account.is_you && (
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          data-testid={`approval-btn-${account.email}`}
          onClick={() => onApproval(account, !account.approved)}
          className="h-8 rounded-sm border-[#262626] bg-transparent hover:bg-[#1F1F1F] uppercase tracking-widest text-[10px]"
        >
          {account.approved ? "Revoke" : "Approve"}
        </Button>
      )}
      {!account.is_you && (
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          data-testid={`remove-account-btn-${account.email}`}
          onClick={() => onRemove(account)}
          className="h-8 rounded-sm border-[#262626] bg-transparent hover:bg-[#FF3B30]/15 hover:text-[#FF3B30]"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
    </span>
  </div>
);

export const AccountsDialog = () => {
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const pendingCount = accounts.filter((a) => !a.approved).length;

  const load = async () => {
    try {
      setAccounts(await authApi.users());
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onOpenChange = (next) => {
    setOpen(next);
    if (next) load();
  };

  const remove = async (account) => {
    setBusy(true);
    try {
      await authApi.deleteUser(account.id);
      toast.success(`Removed ${account.email}`);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const setApproval = async (account, approved) => {
    setBusy(true);
    try {
      await authApi.setApproval(account.id, approved);
      toast.success(`${account.email} ${approved ? "approved" : "approval revoked"}`);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="accounts-btn"
          className="h-9 rounded-sm border-[#262626] bg-transparent hover:bg-[#1F1F1F] uppercase tracking-widest text-xs gap-2"
        >
          <Users className="w-4 h-4" />
          <span className="hidden lg:inline">Accounts</span>
          {pendingCount > 0 && (
            <span
              data-testid="pending-approval-count"
              className="ml-1 px-1.5 py-0.5 rounded-sm bg-[#FFD60A] text-[#0A0A0A] text-[10px] font-bold"
            >
              {pendingCount}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#141414] border-[#262626] rounded-sm" data-testid="accounts-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Operator Accounts</DialogTitle>
          <DialogDescription className="text-sm text-[#A3A3A3]">
            {pendingCount > 0
              ? `${pendingCount} account${pendingCount > 1 ? "s" : ""} waiting for your approval — listed first.`
              : "Everyone with access to this lot. Remove any account you don't recognise."}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col divide-y divide-[#262626] max-h-[60vh] overflow-y-auto">
          {accounts.length === 0 && <p className="text-sm text-[#A3A3A3] py-2">No accounts loaded.</p>}
          {accounts.map((a) => (
            <AccountRow key={a.id} account={a} busy={busy} onRemove={remove} onApproval={setApproval} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
